import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Button,
 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Description as DocumentIcon,
 
  Delete as DeleteIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import documentoService from '../../services/documentoService';
import plantillaService from '../../services/plantillaService';
import especialidadService from '../../services/especialidadService';
import historiaService from '../../services/historiaService'; // <-- NUEVA LÍNEA
import FileUpload from '../forms/FileUpload';
import FormularioSelector from './FormularioSelector';

/**
 * Componente para gestionar documentos por especialidad
 * @param {Object} props - Propiedades del componente
 * @param {number} props.historiaId - ID de la historia clínica
 * @param {number} props.especialidadId - ID de la especialidad
 * @param {Object} props.especialidad - Objeto con datos de la especialidad (opcional)
 * @param {number} props.etapaId - ID de la etapa actual (para referencia)
 * @param {number} props.pacienteId - ID del paciente
 * @param {Array} props.documentosExistentes - Lista de documentos ya existentes (opcional)
 * @param {Function} props.onDocumentoCreado - Callback cuando se crea un documento
 * @param {Function} props.onDocumentoEliminado
 * @returns {JSX.Element} Componente React
 */
const DocumentosEspecialidadList = ({
  historiaId,
  especialidadId,
  especialidad: especialidadProp,
  etapaId,
  pacienteId,
  documentosExistentes = [],
  onDocumentoCreado,
  onDocumentoEliminado 
}) => {
  // Estados
  const [cargando, setCargando] = useState(true);
  const [especialidad, setEspecialidad] = useState(especialidadProp || null);
  const [plantillasEspecialidad, setPlantillasEspecialidad] = useState([]);
  const [documentos, setDocumentos] = useState(documentosExistentes || []);
  const [categorias, setCategorias] = useState([]);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openFormularioDialog, setOpenFormularioDialog] = useState(false);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [estadoDocumento, setEstadoDocumento] = useState('Pendiente');
  const [requiereFirma, setRequiereFirma] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Referencias para prevenir bucles y guardar estado
  const isInitialMount = useRef(true);
  const callbackTriggered = useRef(false);
  const documentosRef = useRef(documentosExistentes || []);
  const callbackTimeoutRef = useRef(null);
  
  // Actualizar referencia cuando cambian los documentos externamente
  useEffect(() => {
    if (!isInitialMount.current) {
      documentosRef.current = documentosExistentes;
    }
  }, [documentosExistentes]);
  
  // Cargar datos iniciales solo una vez al montar
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setCargando(true);
        setError(null);
        
        // Si no tenemos la especialidad, cargarla
        if (!especialidad && especialidadId) {
          try {
            const resEspecialidad = await especialidadService.getById(especialidadId);
            setEspecialidad(resEspecialidad.data);
          } catch (error) {
            console.error('Error al cargar especialidad:', error);
          }
        }
        
        // Cargar plantillas asociadas a la especialidad
        if (especialidadId) {
            try {
                const resPlantillas = await plantillaService.getByEspecialidad(especialidadId);
                setPlantillasEspecialidad(resPlantillas.data || []);
            } catch (error) {
                console.error('Error al cargar plantillas por especialidad:', error);
                setPlantillasEspecialidad([]);
            }
        }
        
        // Si no tenemos documentos preexistentes, cargarlos
        if (documentosExistentes.length === 0 && historiaId) {
          try {
            // Comprobamos si ya cargamos documentos internamente para evitar recargas innecesarias
            if (documentosRef.current.length === 0) {
              const resDocumentos = await documentoService.getDocumentosByHistoria(historiaId);
              const docs = Array.isArray(resDocumentos) ? resDocumentos : 
                        Array.isArray(resDocumentos.data) ? resDocumentos.data : [];
              
              setDocumentos(docs);
              documentosRef.current = docs;
            }
          } catch (error) {
            console.error('Error al cargar documentos:', error);
            setDocumentos([]);
          }
        }
        
        // Cargar categorías
        try {
          const resCategorias = await documentoService.getTiposDocumentos();
          setCategorias(Array.isArray(resCategorias) ? resCategorias : 
                        Array.isArray(resCategorias.data) ? resCategorias.data : []);
        } catch (error) {
          console.error('Error al cargar categorías:', error);
          setCategorias([]);
        }
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('Error al cargar datos. Por favor, intente nuevamente.');
      } finally {
        setCargando(false);
        // Marcar que no es montaje inicial después de la primera carga
        isInitialMount.current = false;
      }
    };
    
    cargarDatos();
    
    // Limpiar timeouts al desmontar
    return () => {
      if (callbackTimeoutRef.current) {
        clearTimeout(callbackTimeoutRef.current);
      }
    };
  }, [historiaId, especialidadId]); // Solo dependemos de estos dos props
  
  // Manejar apertura del diálogo de subida
  const handleOpenUploadDialog = (plantilla = null) => {
    setPlantillaSeleccionada(plantilla);
    setDescripcion(plantilla ? plantilla.nombre : '');
    setEstadoDocumento('Pendiente');
    setRequiereFirma(false);
    setArchivo(null);
    setUploadProgress(0);
    setIsUploading(false);
    setOpenUploadDialog(true);
  };
  
  // Manejar cierre del diálogo de subida
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };
  
  // Manejar apertura del selector de formularios
  const handleOpenFormularioDialog = () => {
    setOpenFormularioDialog(true);
  };
  
  // Manejar cierre del selector de formularios
  const handleCloseFormularioDialog = () => {
    setOpenFormularioDialog(false);
  };
  
  // Manejar cambio de archivo seleccionado
  const handleFileChange = (files) => {
    if (files && files.length > 0) {
      setArchivo(files[0]);
    } else {
      setArchivo(null);
    }
  };
  
  // Función segura para invocar callbacks
  const safeCallback = (callback, data) => {
    // Cancelar cualquier timeout pendiente
    if (callbackTimeoutRef.current) {
      clearTimeout(callbackTimeoutRef.current);
    }
    
    // Si el callback ya fue activado recientemente, no volver a llamarlo
    if (callbackTriggered.current) return;
    
    // Marcar que el callback fue activado
    callbackTriggered.current = true;
    
    // Actualizar estado local inmediatamente
    if (callback === onDocumentoCreado) {
      setDocumentos(prev => [data, ...prev]);
      documentosRef.current = [data, ...documentosRef.current];
    } else if (callback === onDocumentoEliminado) {
      setDocumentos(prev => prev.filter(doc => doc.id !== data));
      documentosRef.current = documentosRef.current.filter(doc => doc.id !== data);
    }
    
    // Invocar callback con delay para prevenir bucles
    callbackTimeoutRef.current = setTimeout(() => {
      if (typeof callback === 'function' && !isInitialMount.current) {
        try {
          callback(data);
        } catch (error) {
          console.error('Error en callback:', error);
        }
      }
      
      // Permitir activar el callback nuevamente después de 500ms
      callbackTimeoutRef.current = setTimeout(() => {
        callbackTriggered.current = false;
      }, 500);
    }, 100);
  };
  
  // Manejar subida del documento
const handleUploadDocument = async () => {
  if (!archivo) {
    setError('Debe seleccionar un archivo');
    return;
  }
  
  try {
    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    // ✅ Obtener usuario_id del localStorage
    let usuarioId = null;
    try {
      const userString = localStorage.getItem('user');
      if (userString) {
        const userObj = JSON.parse(userString);
        usuarioId = userObj.id;
       } else {
        console.warn("⚠️ No se encontró información de usuario en localStorage");
      }
    } catch (err) {
      console.error("Error al obtener usuario:", err);
    }
    
    // ✅ Usar pacienteId de props (NO llamar a historiaService.getHistoria)
    const pacienteIdFinal = pacienteId || 1; // Usar prop directamente
    const usuarioIdFinal = usuarioId || 1;   // Fallback si no está en localStorage
    
 

    // ✅ Crear FormData con todos los campos necesarios
    const formData = new FormData();
    
    // Campos obligatorios del modelo
    formData.append('file', archivo);
    formData.append('paciente_id', pacienteIdFinal);
    formData.append('historia_clinica_id', historiaId);
    formData.append('subido_por', usuarioIdFinal);
    
    // Campos opcionales
    if (especialidadId) {
      formData.append('especialidad_id', especialidadId);
    }
    if (etapaId) {
      formData.append('etapa_id', etapaId);
    }
    formData.append('descripcion', descripcion || archivo.name);
    formData.append('estado', estadoDocumento || 'Pendiente');
    formData.append('requiere_firma', requiereFirma ? 'true' : 'false');
    
    if (plantillaSeleccionada?.id) {
      formData.append('plantilla_id', plantillaSeleccionada.id);
    }

    // Debug: Mostrar contenido del FormData
     for (let [key, value] of formData.entries()) {
     }

    // ✅ Configurar progreso de subida
    const config = {
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
         }
      }
    };

    // ✅ Subir documento
     const response = await documentoService.subirDocumento(formData, config);
    
 
// Manejar caso específico donde data es undefined pero el mensaje indica éxito
let nuevoDocumento = null;

if (response && response.mensaje && response.mensaje.includes('correctamente')) {
  // Caso: el documento se subió pero data es undefined
  // Crear objeto documento temporal con los datos que tenemos
  nuevoDocumento = {
    id: Date.now(), // ID temporal
    nombre: archivo.name,
    tipo: archivo.type,
    tamano: archivo.size,
    descripcion: descripcion || archivo.name,
    fecha_subida: new Date().toISOString(),
    estado: estadoDocumento || 'Pendiente',
    requiere_firma: requiereFirma,
    // Datos que enviamos
    paciente_id: pacienteId,
    historia_clinica_id: historiaId,
    especialidad_id: especialidadId,
    etapa_id: etapaId,
    subido_por: usuarioId
  };
 } else if (response) {
  // Casos normales (cuando data existe)
  if (response.success && response.data) {
    nuevoDocumento = response.data;
  } else if (response.data && typeof response.data === 'object') {
    nuevoDocumento = response.data;
  } else if (response.id || response.nombre) {
    nuevoDocumento = response;
  } else if (response.documento) {
    nuevoDocumento = response.documento;
  }
}

 
if (nuevoDocumento) {
  // Actualizar lista local
  setDocumentos(prev => [nuevoDocumento, ...prev]);
  
  // Notificar al componente padre
  if (typeof onDocumentoCreado === 'function') {
     onDocumentoCreado(nuevoDocumento);
  }
  
  // Cerrar diálogo
  handleCloseUploadDialog();
  
   
} else {
  console.error('❌ No se pudo procesar la respuesta:', response);
  throw new Error('Error al procesar la respuesta del servidor');
}

  } catch (error) {
    console.error('❌ Error detallado al subir documento:', error);
    
    // Mensaje de error específico
    let errorMsg = 'Error al subir el documento. Por favor, intente nuevamente.';
    
    if (error.message) {
      errorMsg = error.message;
    } else if (error.response?.data?.message) {
      errorMsg = error.response.data.message;
    }
    
    setError(errorMsg);
    setUploadProgress(0);
    
  } finally {
    setIsUploading(false);
  }
};
  
  // Manejar selección de formulario
const handleFormularioSelect = (formularioId) => {
  handleCloseFormularioDialog();
  
  // Navegar al editor de formulario
  navigate(`/sistema/historias/${historiaId}/formularios/${formularioId}/editar`);
};

  const handleVerPdfGenerado = (formularioId, respuestaId) => {
  // Aquí puedes abrir un diálogo con FormularioPdfViewer o navegar a una ruta específica
  navigate(`/sistema/historias/${historiaId}/formularios/${formularioId}/respuesta/${respuestaId}/pdf`);
};

  // Manejar descarga de documento
  const handleDownloadDocument = async (documentoId) => {
    try {
      // Usar el método download que maneja la descarga completa
      await documentoService.download(documentoId);
    } catch (error) {
      console.error('Error al descargar documento:', error);
      setError('Error al descargar el documento. Por favor, intente nuevamente.');
    }
  };
  
 // visualización de documento SUBIDOS
const handleViewDocument = async (documentoId) => {
  try {
    // Usar documentoService que ya tiene configurados los interceptores
    const result = await documentoService.previewDocument(documentoId);
    
    if (result.success && result.url) {
      // Abrir en nueva ventana
      window.open(result.url, '_blank');
      
      // Limpiar URL después de un tiempo
      setTimeout(() => URL.revokeObjectURL(result.url), 1000);
    } else {
      throw new Error('No se pudo generar la previsualización');
    }
    
  } catch (error) {
    console.error("Error al abrir documento:", error);
    setError("No se pudo abrir el documento para visualización");
  }
};

  // Manejar edición de documento (para documentos generados por formularios)
  const handleEditDocument = (documento) => {
    if (documento.plantilla_id) {
      navigate(`/historias/${historiaId}/formularios/${documento.plantilla_id}/editar?documento=${documento.id}`);
    }
  };
  
  // Manejar eliminación de documento
  const handleDeleteDocument = async (documentoId) => {
    if (!window.confirm("¿Está seguro que desea eliminar este documento?")) {
      return;
    }
    
    try {
      await documentoService.eliminarDocumento(documentoId);  // ✅ MÉTODO CORRECTO
      
      // Usar callback seguro para notificar
      safeCallback(onDocumentoEliminado, documentoId);
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      setError('Error al eliminar el documento. Por favor, intente nuevamente.');
    }
  };
  
  // Renderizar icono según tipo de documento
  const renderIconoDocumento = (tipo) => {
    if (tipo === 'PDF' || (tipo && tipo.toLowerCase && tipo.toLowerCase().includes('pdf'))) {
      return <DocumentIcon color="error" />;
    } else if (tipo === 'FORM' || tipo === 'Formulario') {
      return <AssignmentIcon color="primary" />;
    }
    return <DocumentIcon />;
  };
  
  // Renderizar documentos
  const renderDocumentos = () => {
    // Usar documentos del estado local, que ya incluirá cualquier cambio
    const docsToRender = documentos;
    
    if (docsToRender.length === 0) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No hay documentos disponibles para esta especialidad.
          </Typography>
        </Paper>
      );
    }
    
    // Separar documentos en dos grupos: PDFs y generados por formularios
    const documentosPDF = docsToRender.filter(doc => 
      !doc.plantilla_id && 
      (doc.tipo === 'PDF' || (doc.tipo && doc.tipo.toLowerCase && doc.tipo.toLowerCase().includes('pdf')))
    );
    
    const documentosFormulario = docsToRender.filter(doc => 
      doc.plantilla_id || doc.tipo === 'FORM' || doc.tipo === 'Formulario'
    );
    
    return (
      <>
        {/* Documentos PDF */}
        {documentosPDF.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title="Documentos Subidos" 
              subheader="Archivos PDF y otros documentos subidos manualmente"
            />
            <Divider />
            <CardContent>
              <List>
                {documentosPDF.map(documento => (
                  <ListItem key={documento.id} divider>
                    <ListItemIcon>
                      {renderIconoDocumento(documento.tipo)}
                    </ListItemIcon>
                    <ListItemText
                      primary={documento.nombre}
                      secondary={
                        <>
                          <Typography variant="caption" color="textSecondary">
                            Subido: {new Date(documento.fecha_subida).toLocaleDateString()}
                          </Typography>
                          {documento.descripcion && (
                            <Typography variant="body2">
                              {documento.descripcion}
                            </Typography>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Ver documento">
                        <IconButton
                          edge="end"
                          onClick={() => handleViewDocument(documento.id)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Descargar">
                        <IconButton
                          edge="end"
                          onClick={() => handleDownloadDocument(documento.id)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleDeleteDocument(documento.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}
        
        {/* Documentos de Formularios */}
        {documentosFormulario.length > 0 && (
          <Card>
            <CardHeader 
              title="Documentos Generados por Formularios" 
              subheader="Formularios completados y documentos generados"
            />
            <Divider />
            <CardContent>
              <List>
                {documentosFormulario.map(documento => (
                  <ListItem key={documento.id} divider>
                    <ListItemIcon>
                      {renderIconoDocumento(documento.tipo)}
                    </ListItemIcon>
                    <ListItemText
                      primary={documento.nombre}
                      secondary={
                        <>
                          <Typography variant="caption" color="textSecondary">
                            Creado: {new Date(documento.fecha_subida).toLocaleDateString()}
                          </Typography>
                          {documento.descripcion && (
                            <Typography variant="body2">
                              {documento.descripcion}
                            </Typography>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Editar">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => handleEditDocument(documento)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver documento">
                        <IconButton
                          edge="end"
                          onClick={() => handleViewDocument(documento.id)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Descargar">
                        <IconButton
                          edge="end"
                          onClick={() => handleDownloadDocument(documento.id)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleDeleteDocument(documento.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}
      </>
    );
  };
  
  // Si está cargando, mostrar indicador de progreso
  if (cargando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">
          Documentos de {especialidad?.nombre || 'la especialidad'}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleOpenUploadDialog}
            sx={{ mr: 1 }}
          >
            Subir Documento
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AssignmentIcon />}
            onClick={handleOpenFormularioDialog}
          >
            Completar Formulario
          </Button>
        </Box>
      </Box>
      
      {/* Renderizar documentos */}
      {renderDocumentos()}
      
      {/* Diálogo para subir documento */}
      <Dialog
        open={openUploadDialog}
        onClose={!isUploading ? handleCloseUploadDialog : undefined}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {plantillaSeleccionada 
            ? `Subir documento: ${plantillaSeleccionada.nombre}` 
            : 'Subir documento'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                required
                disabled={isUploading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Estado del documento</InputLabel>
                <Select
                  value={estadoDocumento}
                  onChange={(e) => setEstadoDocumento(e.target.value)}
                  label="Estado del documento"
                  disabled={isUploading}
                >
                  <MenuItem value="Pendiente">Pendiente</MenuItem>
                  <MenuItem value="Completado">Completado</MenuItem>
                  <MenuItem value="Revisado">Revisado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={requiereFirma}
                    onChange={(e) => setRequiereFirma(e.target.checked)}
                    disabled={isUploading}
                  />
                }
                label="Requiere firma digital"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Seleccionar archivo
                </Typography>
                
                <FileUpload
                  onFileSelected={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  maxFiles={1}
                  maxSize={10 * 1024 * 1024} // 10 MB
                  disabled={isUploading}
                />
              </Paper>
            </Grid>
            
            {isUploading && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Subiendo documento: {uploadProgress}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                  />
                </Box>
              </Grid>
            )}
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {error}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCloseUploadDialog}
            disabled={isUploading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleUploadDocument}
            variant="contained"
            disabled={!archivo || isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {isUploading ? 'Subiendo...' : 'Subir documento'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo para seleccionar formulario */}
      <Dialog
        open={openFormularioDialog}
        onClose={handleCloseFormularioDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Seleccionar Formulario</DialogTitle>
        <DialogContent>
          <FormularioSelector 
            especialidadId={especialidadId}
            historiaId={historiaId}
            onFormularioSelect={handleFormularioSelect}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFormularioDialog}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentosEspecialidadList;